import { useEffect, useState } from 'react';
import { apiFetch } from '../lib/api';
import { useAuthStore } from '../store/auth.store';
import { useAceitePendenteStore } from '../store/aceite-pendente.store';
import { broadcastAuth } from '../lib/auth-broadcast';

/**
 * Modal bloqueante de re-aceite (REQ-012 Adendo / AB#63 — MAJOR).
 *
 * Aberto quando o `apiFetch` recebe 403 com code='ACEITE_PENDENTE' e popula o
 * store `useAceitePendenteStore`. Fechável só por "Aceitar" ou "Sair".
 *
 * Carrega o documento ativo via GET público `/documentos-legais/publico/:tipo`
 * (não exige sessão íntegra — o usuário ainda pode estar autenticado mas
 * bloqueado pelo guard).
 */

const TIPO_LABEL: Record<string, string> = {
  TERMOS_USO: 'Termos de Uso',
  PRIVACIDADE: 'Política de Privacidade',
  DPA: 'DPA',
};

interface DocumentoPublico {
  found: boolean;
  id?: number;
  tipo?: string;
  versao?: string;
  conteudo?: string;
  resumo_mudancas?: string | null;
  publicado_em?: string;
}

export function AceitePendenteModal(): JSX.Element | null {
  const pendente = useAceitePendenteStore((s) => s.pendente);
  const limpar = useAceitePendenteStore((s) => s.limpar);
  const logout = useAuthStore((s) => s.logout);

  const [doc, setDoc] = useState<DocumentoPublico | null>(null);
  const [carregando, setCarregando] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState('');

  useEffect(() => {
    if (!pendente) {
      setDoc(null);
      return;
    }
    let cancelado = false;
    (async () => {
      setCarregando(true);
      setErro('');
      try {
        const API_URL = `${import.meta.env.VITE_API_URL ?? 'http://localhost:8000'}/api`;
        const res = await fetch(`${API_URL}/documentos-legais/publico/${pendente.tipo}`);
        if (!res.ok || cancelado) return;
        const data = (await res.json()) as DocumentoPublico;
        if (!cancelado) setDoc(data.found ? data : null);
      } catch {
        if (!cancelado) setErro('Falha ao carregar o documento.');
      } finally {
        if (!cancelado) setCarregando(false);
      }
    })();
    return () => {
      cancelado = true;
    };
  }, [pendente]);

  if (!pendente) return null;

  async function aceitar(): Promise<void> {
    if (!pendente) return;
    setSalvando(true);
    setErro('');
    try {
      const res = await apiFetch('/minha-conta/aceite-legal', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ documento_id: pendente.documento_id }),
      });
      if (!res.ok) {
        setErro('Falha ao registrar aceite.');
        return;
      }
      limpar();
      // Recarrega para revalidar a sessão (guard agora libera).
      window.location.reload();
    } catch {
      setErro('Erro de conexão.');
    } finally {
      setSalvando(false);
    }
  }

  async function sair(): Promise<void> {
    await logout();
    broadcastAuth('LOGOUT');
    limpar();
    window.location.href = '/login';
  }

  const tipoLabel = TIPO_LABEL[pendente.tipo] ?? pendente.tipo;

  return (
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-900/70 p-4"
    >
      <div className="flex max-h-[90vh] w-full max-w-2xl flex-col rounded-lg bg-white shadow-2xl">
        <header className="border-b border-slate-200 px-6 py-4">
          <p className="text-xs font-semibold uppercase tracking-wide text-brand-600">
            Aceite obrigatório
          </p>
          <h1 className="mt-1 text-xl font-bold text-slate-900">
            Atualizamos os {tipoLabel}
          </h1>
          <p className="mt-1 text-xs text-slate-500">Versão {pendente.versao}</p>
        </header>
        <div className="flex-1 overflow-auto px-6 py-4">
          {carregando && <p className="text-sm text-slate-400">Carregando o documento…</p>}
          {erro && (
            <p role="alert" className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-800">
              {erro}
            </p>
          )}
          {doc?.resumo_mudancas && (
            <div className="mb-4 rounded-md border-l-4 border-brand-500 bg-brand-50 px-4 py-3 text-sm text-slate-700">
              <strong>Resumo das mudanças:</strong>{' '}
              <span className="whitespace-pre-line">{doc.resumo_mudancas}</span>
            </div>
          )}
          {doc?.conteudo && (
            <article
              className="prose prose-sm max-w-none text-slate-800"
              dangerouslySetInnerHTML={{ __html: doc.conteudo }}
            />
          )}
        </div>
        <footer className="flex items-center justify-between gap-3 border-t border-slate-200 bg-slate-50 px-6 py-4">
          <button
            type="button"
            onClick={() => void sair()}
            disabled={salvando}
            className="text-sm font-medium text-slate-500 hover:text-slate-800 disabled:opacity-60"
          >
            Sair sem aceitar
          </button>
          <button
            type="button"
            onClick={() => void aceitar()}
            disabled={salvando || carregando}
            className="inline-flex h-10 items-center rounded-md bg-brand-500 px-5 text-sm font-bold text-white hover:bg-brand-600 disabled:opacity-60"
          >
            {salvando ? 'Salvando…' : 'Li e aceito os novos termos'}
          </button>
        </footer>
      </div>
    </div>
  );
}
